/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, observable, set } from 'mobx';
import { AppStore } from 'src/core/stores/app/AppStore';
import { AppSettings, AzureAccount } from 'src/core/stores/app/types';
import { OmitFunctions } from 'src/core/stores/types';
import { createAppVersionStoreHouse } from 'src/luis/core/stores/appVersionStoreHouse';
import { ConversationPublishSlotStore, ConversationPublishSlotStoreData } from 'src/luis/core/stores/ConversationPublishSlotStore';
import { createDomainStoreHouse } from 'src/luis/core/stores/domainStoreHouse';
import { createPrebuiltEntityStoreHouse } from 'src/luis/core/stores/prebuiltEntityStoreHouse';
import { PublishSlotType } from 'src/luis/types/PublishSlotType';
import { AppVersionStoreHouse } from 'src/luis/types/stores/apps/AppVersionStoreHouse';
import { DomainStoreHouse } from 'src/luis/types/stores/domains/DomainStoreHouse';
import { PrebuiltEntityStoreHouse } from 'src/luis/types/stores/entities/PrebuiltEntityStoreHouse';

export type ConversationAppData = Partial<OmitFunctions<Omit<LuisAppStore, 'id' | 'appType' | 'modelStores'>>>;

type PublishSlots = { staging: ConversationPublishSlotStore | null; production: ConversationPublishSlotStore | null };

export class LuisAppStore extends AppStore {
    public readonly endpointHitsCount: number;

    @observable public activeVersion: string;

    @observable public domainStoreHouse: DomainStoreHouse;

    @observable public appVersionStoreHouse: AppVersionStoreHouse;

    @observable public prebuiltEntityStoreHouse: PrebuiltEntityStoreHouse;

    @observable public appSettings: AppSettings;

    @observable public publishSlots: PublishSlots;

    @observable public azureAccounts: AzureAccount[];

    constructor(id: string, appData: Partial<ConversationAppData>) {
        super(id, 'conversation', appData);

        this.endpointHitsCount = appData.endpointHitsCount;

        this.activeVersion = appData.activeVersion || '';
        this.appSettings = appData.appSettings;
        this.publishSlots = appData.publishSlots || { staging: null, production: null };
        this.azureAccounts = appData.azureAccounts || [];

        this.appVersionStoreHouse = createAppVersionStoreHouse();
        this.prebuiltEntityStoreHouse = createPrebuiltEntityStoreHouse();
        this.domainStoreHouse = createDomainStoreHouse();
    }

    get currentAppVersionStore() {
        return this.appVersionStoreHouse.items.find(item => item.id === this.activeVersion);
    }

    @action
    public rename(name: string) {
        this.name = name;
    }

    @action
    public setActiveVersion(activeVersion: string): void {
        this.activeVersion = activeVersion;
    }

    @action
    public setPublishSlot(slotName: PublishSlotType, slotData: ConversationPublishSlotStoreData) {
        this.publishSlots[slotName] = new ConversationPublishSlotStore(slotData);
    }

    @action
    public setAppSettings(appSettings: AppSettings) {
        this.appSettings = appSettings;
    }

    @action
    public setAzureAccounts(azureAccounts: AzureAccount[]) {
        this.azureAccounts = azureAccounts;
    }

    @action
    public setPublishSlotMetadata(isStaging: boolean, metadata: ConversationPublishSlotStoreData) {
        const slotKey = isStaging ? 'staging' : 'production';
        if (this.publishSlots[slotKey]) {
            set(this.publishSlots[slotKey], observable(metadata));

            return;
        }

        this.publishSlots[slotKey] = new ConversationPublishSlotStore(metadata);
    }
}
